import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, FileText, Check, X, Quote } from "lucide-react";
import type { AiInvoiceResult } from "./types";

export interface ResolvedLine {
  system_name: string;
  work_type_name: string;
  cost: string;
  is_labour: boolean;
  is_gst: boolean;
  notes: string;
}

export interface ResolvedHeader {
  supplier?: string;
  registration?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
}


interface ReviewRow extends ResolvedLine {
  accepted: boolean;
  source_text: string;
}


interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  previewUrl: string | null;
  fileMime: string | null;
  fileName: string | null;
  result: AiInvoiceResult | null;
  systems: string[];
  workTypes: string[];
  onApply: (lines: ResolvedLine[], header: ResolvedHeader) => void;
}

const matchOption = (val: string, options: string[]) => {
  const lower = (val || "").toLowerCase();
  return (
    options.find((o) => o.toLowerCase() === lower) ||
    options.find((o) => o.toLowerCase().includes(lower) || (lower && lower.includes(o.toLowerCase()))) ||
    ""
  );
};

const MaintenanceAIReviewDialog: React.FC<Props> = ({
  open, onOpenChange, previewUrl, fileMime, fileName, result, systems, workTypes, onApply,
}) => {
  const [rows, setRows] = useState<ReviewRow[]>([]);

  // Confirmable header fields (plate / invoice # / date) + per-field accept toggles.
  const [registration, setRegistration] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [supplier, setSupplier] = useState("");
  const [acceptRego, setAcceptRego] = useState(true);
  const [acceptInvNo, setAcceptInvNo] = useState(true);
  const [acceptDate, setAcceptDate] = useState(true);
  const [acceptSupplier, setAcceptSupplier] = useState(true);

  useEffect(() => {
    if (!result) {
      setRows([]);
      return;
    }
    setRegistration(result.registration || "");
    setInvoiceNumber(result.invoice_number || "");
    setInvoiceDate(result.invoice_date || "");
    setSupplier(result.supplier || "");
    setAcceptRego(!!result.registration);
    setAcceptInvNo(!!result.invoice_number);
    setAcceptDate(!!result.invoice_date);
    setAcceptSupplier(!!result.supplier);

    const labourType = workTypes.find((w) => w.toLowerCase() === "labour");
    const gstType = workTypes.find((w) => w.toLowerCase() === "gst");
    const gstSystem = systems.find((s) => s.toLowerCase() === "gst");

    const built: ReviewRow[] = (result.line_items || []).map((li) => ({
      accepted: true,
      source_text: li.source_text || li.description || "",
      system_name: matchOption(li.suggested_system, systems),
      work_type_name: li.is_labour ? (labourType || matchOption(li.suggested_work_type, workTypes)) : matchOption(li.suggested_work_type, workTypes),
      cost: li.amount != null ? String(li.amount) : "",
      is_labour: !!li.is_labour,
      is_gst: false,
      notes: li.description || "",
    }));

    // GST is now treated as its own line item (like parts and labour).
    // Prefer an explicit GST amount; otherwise infer it from (total − subtotal).
    const lineSum = (result.line_items || []).reduce((s, li) => s + (Number(li.amount) || 0), 0);
    const subtotal = result.subtotal != null ? Number(result.subtotal) : lineSum;
    let gst = result.gst_amount != null ? Number(result.gst_amount) : NaN;
    if (!Number.isFinite(gst) || gst <= 0) {
      const inferred = result.total_amount != null ? Number(result.total_amount) - subtotal : NaN;
      gst = Number.isFinite(inferred) && inferred > 0.009 ? inferred : 0;
    }
    gst = Math.round(gst * 100) / 100;
    if (gst > 0) {
      built.push({
        accepted: true,
        source_text: "GST charged over the full invoice",
        system_name: gstSystem || "GST",
        work_type_name: gstType || "GST",
        cost: String(gst),
        is_labour: false,
        is_gst: true,
        notes: "GST charged over the full invoice",
      });
    }

    setRows(built);
  }, [result, systems, workTypes]);


  const update = (idx: number, patch: Partial<ReviewRow>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const isPdf = (fileMime || "").includes("pdf") || (fileName || "").toLowerCase().endsWith(".pdf");
  const isImage = (fileMime || "").startsWith("image/");

  const acceptedRows = rows.filter((r) => r.accepted);
  const acceptedCount = acceptedRows.length;
  const partsTotal = acceptedRows.filter((r) => !r.is_gst && !r.is_labour).reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const labourTotal = acceptedRows.filter((r) => !r.is_gst && r.is_labour).reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const gstTotal = acceptedRows.filter((r) => r.is_gst).reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const acceptedTotal = partsTotal + labourTotal + gstTotal;

  const apply = () => {
    const accepted = acceptedRows.map((r) => ({
      system_name: r.system_name,
      work_type_name: r.work_type_name,
      cost: r.cost,
      is_labour: r.is_labour,
      is_gst: r.is_gst,
      notes: r.notes,
    }));
    onApply(accepted, {
      supplier: acceptSupplier ? (supplier.trim() || undefined) : undefined,
      registration: acceptRego ? (registration.trim() || undefined) : undefined,
      invoiceNumber: acceptInvNo ? (invoiceNumber.trim() || undefined) : undefined,
      invoiceDate: acceptDate ? (invoiceDate.trim() || undefined) : undefined,
    });

    onOpenChange(false);
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            Review AI-read invoice
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-0">
          {/* Preview pane */}
          <div className="flex flex-col min-h-0 rounded-lg border border-border bg-black/30">
            <div className="px-3 py-2 border-b border-border flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="w-4 h-4" />
              <span className="truncate">{fileName || "Invoice"}</span>
            </div>
            <div className="flex-1 overflow-auto p-2">
              {previewUrl && isImage && (
                <img src={previewUrl} alt="invoice" className="w-full h-auto rounded" />
              )}
              {previewUrl && isPdf && (
                <object data={previewUrl} type="application/pdf" className="w-full h-[60vh] rounded">
                  <iframe src={previewUrl} title="invoice-pdf" className="w-full h-[60vh] rounded" />
                </object>
              )}
              {previewUrl && !isImage && !isPdf && (
                <div className="text-sm text-muted-foreground p-4">Preview not available for this file type.</div>
              )}
            </div>
            {/* Confirmable invoice header details */}
            <div className="px-3 py-3 border-t border-border space-y-2.5 bg-black/20">
              <p className="text-xs font-medium text-yellow-500">Confirm invoice details</p>

              {/* Registration / plate */}
              <div className={`rounded-md border p-2 transition-colors ${acceptRego ? "border-yellow-700/50 bg-yellow-950/10" : "border-border opacity-60"}`}>
                <label className="flex items-center gap-2 text-xs mb-1.5 cursor-pointer">
                  <Checkbox checked={acceptRego} onCheckedChange={(c) => setAcceptRego(Boolean(c))} />
                  <span className="font-medium">Number plate / Rego</span>
                </label>
                <Input value={registration} onChange={(e) => setRegistration(e.target.value)} className="h-8 text-sm" placeholder="e.g. PZU288" disabled={!acceptRego} />
              </div>

              <div className="grid grid-cols-2 gap-2">
                {/* Invoice number */}
                <div className={`rounded-md border p-2 transition-colors ${acceptInvNo ? "border-yellow-700/50 bg-yellow-950/10" : "border-border opacity-60"}`}>
                  <label className="flex items-center gap-2 text-xs mb-1.5 cursor-pointer">
                    <Checkbox checked={acceptInvNo} onCheckedChange={(c) => setAcceptInvNo(Boolean(c))} />
                    <span className="font-medium">Invoice #</span>
                  </label>
                  <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className="h-8 text-sm" placeholder="INV-0000" disabled={!acceptInvNo} />
                </div>

                {/* Invoice date */}
                <div className={`rounded-md border p-2 transition-colors ${acceptDate ? "border-yellow-700/50 bg-yellow-950/10" : "border-border opacity-60"}`}>
                  <label className="flex items-center gap-2 text-xs mb-1.5 cursor-pointer">
                    <Checkbox checked={acceptDate} onCheckedChange={(c) => setAcceptDate(Boolean(c))} />
                    <span className="font-medium">Date</span>
                  </label>
                  <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="h-8 text-sm" disabled={!acceptDate} />
                </div>
              </div>

              {/* Supplier */}
              <div className={`rounded-md border p-2 transition-colors ${acceptSupplier ? "border-yellow-700/50 bg-yellow-950/10" : "border-border opacity-60"}`}>
                <label className="flex items-center gap-2 text-xs mb-1.5 cursor-pointer">
                  <Checkbox checked={acceptSupplier} onCheckedChange={(c) => setAcceptSupplier(Boolean(c))} />
                  <span className="font-medium">Supplier / Service centre</span>
                </label>
                <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} className="h-8 text-sm" placeholder="e.g. City Auto" disabled={!acceptSupplier} />
              </div>

              {/* Totals — GST is now an item in the list on the right */}
              <div className="rounded-md border border-emerald-700/40 bg-emerald-950/10 p-2 space-y-0.5">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Parts</span>
                  <span className="text-blue-300">${partsTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Labour</span>
                  <span className="text-orange-300">${labourTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">GST</span>
                  <span className="text-emerald-300">${gstTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs font-semibold border-t border-emerald-700/30 pt-1 mt-1">
                  <span>Invoice total</span>
                  <span className="text-green-400">${acceptedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <p className="text-[10px] text-muted-foreground pt-1">GST is now shown as its own item in the list — accept, edit or decline it like any other line.</p>
              </div>

              <p className="text-[11px] text-muted-foreground">Untick any field to leave the form's existing value unchanged.</p>


            </div>
          </div>

          {/* Items pane */}
          <div className="flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">
                {rows.length} item(s) detected. Accept, edit or decline each before applying.
              </p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => setRows((p) => p.map((r) => ({ ...r, accepted: true })))}>All</Button>
                <Button variant="outline" size="sm" onClick={() => setRows((p) => p.map((r) => ({ ...r, accepted: false })))}>None</Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto space-y-3 pr-1">
              {rows.length === 0 && (
                <div className="text-sm text-muted-foreground p-4 text-center border border-dashed border-border rounded-lg">
                  No line items were detected on this invoice.
                </div>
              )}
              {rows.map((row, idx) => (
                <div
                  key={idx}
                  className={`rounded-lg border p-3 space-y-3 transition-colors ${
                    row.accepted ? "border-yellow-700/50 bg-yellow-950/10" : "border-border bg-background/30 opacity-60"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                      <Checkbox checked={row.accepted} onCheckedChange={(c) => update(idx, { accepted: Boolean(c) })} />
                      {row.accepted ? (
                        <span className="flex items-center gap-1 text-green-400"><Check className="w-3.5 h-3.5" /> Include</span>
                      ) : (
                        <span className="flex items-center gap-1 text-muted-foreground"><X className="w-3.5 h-3.5" /> Skipped</span>
                      )}
                    </label>
                    {row.is_gst
                      ? <Badge className="text-xs bg-emerald-600/20 text-emerald-300 border border-emerald-600/40">GST</Badge>
                      : row.is_labour && <Badge variant="secondary" className="text-xs">Labour</Badge>}

                  </div>

                  {row.source_text && (
                    <div className="flex gap-2 text-xs text-muted-foreground bg-black/20 rounded p-2 border border-border/60">
                      <Quote className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span className="italic">{row.source_text}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4 space-y-1">
                      <Label className="text-xs">Vehicle System</Label>
                      <Select value={row.system_name} onValueChange={(v) => update(idx, { system_name: v })}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="System" /></SelectTrigger>
                        <SelectContent className="max-h-[240px]">
                          {systems.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-4 space-y-1">
                      <Label className="text-xs">Work Type</Label>
                      <Select
                        value={row.work_type_name}
                        onValueChange={(v) => update(idx, { work_type_name: v, is_labour: v.toLowerCase() === "labour" ? true : row.is_labour })}
                      >
                        <SelectTrigger className="h-9"><SelectValue placeholder="Work" /></SelectTrigger>
                        <SelectContent className="max-h-[240px]">
                          {workTypes.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-4 space-y-1">
                      <Label className="text-xs">Cost ($)</Label>
                      <Input type="number" step="0.01" className="h-9" value={row.cost} onChange={(e) => update(idx, { cost: e.target.value })} placeholder="0.00" />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Textarea value={row.notes} onChange={(e) => update(idx, { notes: e.target.value })} rows={2} className="text-sm" />
                  </div>

                  <div className="flex items-center gap-4 flex-wrap">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Checkbox
                        checked={row.is_labour}
                        onCheckedChange={(c) => update(idx, { is_labour: Boolean(c), is_gst: Boolean(c) ? false : row.is_gst })}
                      />
                      Treat as labour charge
                    </label>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Checkbox
                        checked={row.is_gst}
                        onCheckedChange={(c) => {
                          const on = Boolean(c);
                          const gstType = workTypes.find((w) => w.toLowerCase() === "gst");
                          const gstSystem = systems.find((s) => s.toLowerCase() === "gst");
                          update(idx, on
                            ? { is_gst: true, is_labour: false, system_name: gstSystem || "GST", work_type_name: gstType || "GST" }
                            : { is_gst: false });
                        }}
                      />
                      Treat as GST
                    </label>
                  </div>

                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-border pt-3 flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="text-sm">
            <span className="text-muted-foreground">{acceptedCount} of {rows.length} selected · Total: </span>
            <span className="font-semibold text-green-400">
              ${acceptedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={apply} disabled={acceptedCount === 0} className="golden-button">
              <Check className="w-4 h-4 mr-2" /> Apply {acceptedCount} item(s)
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MaintenanceAIReviewDialog;
